import React, { useEffect, useRef, useState } from "react";

import asanaLogo from "./../images/asana.svg";
import teamsLogo from "./../images/microsoftteams.svg";
import outlookLogo from "./../images/outlook.svg";

/**
 * DemoSequence
 * ------------
 * The "Watch BossDrop follow through" demo. One true sequence, five beats, ~31s,
 * looping. Pure CSS + React - no video file, no dependency. Each beat stays up
 * long enough to read (denser beats get longer). The buttons step back, pause or
 * step forward, and the loop also holds while the pointer is on the demo itself.
 * Stepping keeps it playing or paused, whichever it was.
 *
 * Driving it for review/screenshots:
 *   ?beat=1..5   pins a single beat (no auto-advance)
 *   ?motion=off  forces the reduced-motion static composition
 */

const BEATS = [
  {
    id: "signal",
    step: "Signal",
    source: "Asana",
    logo: asanaLogo,
    ms: 5000,
    caption: "A task slips. Checkout release QA sign-off was due Tuesday.",
  },
  {
    id: "decide",
    step: "BossDrop decides",
    source: "BossDrop",
    logo: null,
    ms: 6500,
    caption: "Planner proposes, critic checks, confidence clears the bar.",
  },
  {
    id: "chase",
    step: "It chases",
    source: "Microsoft Teams",
    logo: teamsLogo,
    ms: 5500,
    caption: "It asks Priya what is blocking and when the next update lands.",
  },
  {
    id: "act",
    step: "It acts on the answer",
    source: "Asana · Approval queue",
    logo: asanaLogo,
    ms: 6500,
    caption: "A task for Taylor, and an escalation a human approves.",
  },
  {
    id: "brief",
    step: "Friday",
    source: "Outlook",
    logo: outlookLogo,
    ms: 7500,
    caption: "The executive brief lands, with the decision waiting on you.",
  },
];

function readQuery() {
  if (typeof window === "undefined") return { pinned: null, motionOff: false };
  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch (e) {
    return { pinned: null, motionOff: false };
  }
  const raw = parseInt(params.get("beat"), 10);
  const pinned = raw >= 1 && raw <= BEATS.length ? raw - 1 : null;
  return { pinned, motionOff: params.get("motion") === "off" };
}

export default function DemoSequence() {
  const [{ pinned, motionOff }] = useState(readQuery);
  const [beat, setBeat] = useState(pinned || 0);
  const [reduced, setReduced] = useState(motionOff);
  const [inView, setInView] = useState(true);
  const [hovering, setHovering] = useState(false);
  const [stopped, setStopped] = useState(false);
  const paused = hovering || stopped;
  const rootRef = useRef(null);
  // Time left on the current beat, so a hover pause resumes where it stopped
  // (the CSS progress bar pauses with it via .isPaused).
  const clockRef = useRef({ start: 0, remaining: BEATS[0].ms });

  // Respect the OS reduced-motion setting, live.
  useEffect(() => {
    if (motionOff || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduced(mq.matches);
    sync();
    if (mq.addEventListener) {
      mq.addEventListener("change", sync);
      return () => mq.removeEventListener("change", sync);
    }
    mq.addListener(sync);
    return () => mq.removeListener(sync);
  }, [motionOff]);

  // Don't animate off-screen.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => setInView(e.isIntersecting)),
      { threshold: 0.2 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const isStatic = reduced;
  const running = !isStatic && pinned === null && inView;

  // A new beat starts with its full duration. Declared before the timer effect
  // so it runs after the old timer's cleanup has recorded its remainder.
  useEffect(() => {
    clockRef.current.remaining = (BEATS[beat] || BEATS[0]).ms;
  }, [beat]);

  useEffect(() => {
    if (!running || paused) return;
    const clock = clockRef.current;
    const wait = clock.remaining;
    clock.start = Date.now();
    const id = window.setTimeout(
      () => setBeat((b) => (b + 1) % BEATS.length),
      wait,
    );
    return () => {
      window.clearTimeout(id);
      clock.remaining = Math.max(0, wait - (Date.now() - clock.start));
    };
  }, [running, paused, beat]);

  const cls = [
    "demoSeq",
    isStatic ? "isStatic" : "",
    pinned !== null ? "isPinned" : "",
    paused ? "isPaused" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const active = BEATS[beat] || BEATS[0];
  const step = (by) => setBeat((b) => (b + by + BEATS.length) % BEATS.length);

  return (
    <div
      className={cls}
      ref={rootRef}
      style={{ "--beat-ms": `${active.ms}ms` }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      aria-label="BossDrop in one sequence: a task slips, BossDrop decides, chases the owner, creates a task and escalates, then sends the Friday executive brief."
    >
      <div className="demoTop">
        <div className="demoDots" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <div className="demoTrack" aria-hidden="true">
          {BEATS.map((b, i) => (
            <span
              key={b.id}
              className={
                "demoSeg" +
                (i === beat ? " isOn" : "") +
                (i < beat ? " isDone" : "")
              }
            >
              <i />
            </span>
          ))}
        </div>
        {/* A pinned or static demo has nothing to control. A pointer on the
            buttons doesn't count as holding the demo, or Play would seem to do
            nothing until the pointer left. */}
        {!isStatic && pinned === null && (
          <div
            className="demoControls"
            role="group"
            aria-label="Demo controls"
            onMouseEnter={() => setHovering(false)}
            onMouseLeave={() => setHovering(true)}
          >
            <button
              type="button"
              className="demoControl"
              aria-label="Previous step"
              onClick={() => step(-1)}
            >
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path
                  d="M10 3 5 8l5 5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              className="demoControl"
              aria-label={stopped ? "Play demo" : "Pause demo"}
              onClick={() => setStopped((was) => !was)}
            >
              {stopped ? (
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M5 3v10l8-5z" fill="currentColor" />
                </svg>
              ) : (
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M4.5 3h2.5v10H4.5zM9 3h2.5v10H9z" fill="currentColor" />
                </svg>
              )}
            </button>
            <button
              type="button"
              className="demoControl"
              aria-label="Next step"
              onClick={() => step(1)}
            >
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path
                  d="m6 3 5 5-5 5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      <div className="demoStage">
        {BEATS.map((b, i) => {
          const on = isStatic || i === beat;
          return (
            <section
              key={b.id}
              className={"demoBeat demoBeat--" + b.id + (on ? " isActive" : "")}
              aria-hidden={on ? undefined : "true"}
            >
              <header className="demoBeatHead">
                <span className="demoStep">
                  <b>{"0" + (i + 1)}</b>
                  {b.step}
                </span>
                <span className="demoSource">
                  {b.logo ? (
                    <img src={b.logo} alt="" aria-hidden="true" />
                  ) : (
                    <i className="demoMark" aria-hidden="true">
                      BD
                    </i>
                  )}
                  {b.source}
                </span>
              </header>
              {renderBeat(b.id)}
            </section>
          );
        })}
      </div>

      {/* Read aloud only while paused, so a screen reader isn't interrupted every
          few seconds by the loop. */}
      <p className="demoCaption" aria-live={stopped ? "polite" : "off"}>
        {isStatic ? (
          <>
            <span className="demoCaptionIdx">01 to 05</span>
            One real sequence: signal in, decision, chase, action, Friday brief.
          </>
        ) : (
          <>
            <span className="demoCaptionIdx">{"0" + (beat + 1)} / 05</span>
            <strong>{active.step}.</strong> {active.caption}
          </>
        )}
      </p>
    </div>
  );
}

function renderBeat(id) {
  if (id === "signal") return <BeatSignal />;
  if (id === "decide") return <BeatDecide />;
  if (id === "chase") return <BeatChase />;
  if (id === "act") return <BeatAct />;
  return <BeatBrief />;
}

/* 01 - a task slips in Asana */
function BeatSignal() {
  return (
    <div className="demoPanel">
      <div className="demoListHead">
        <span>Checkout release · 7 open tasks</span>
        <span className="demoColWho">Assignee</span>
        <span className="demoColDue">Due</span>
      </div>

      <div className="demoRow demoIn" style={{ "--d": "0.05s" }}>
        <span className="demoTick demoTickOpen" aria-hidden="true" />
        <span className="demoRowTitle">Vendor SSO keys → staging</span>
        <span className="demoColWho">
          <i className="demoAv demoAvTM" aria-hidden="true">
            TM
          </i>
          Taylor Mendes
        </span>
        <span className="demoColDue">Fri, Sep 25</span>
      </div>

      <div className="demoRow isAlert demoIn" style={{ "--d": "0.15s" }}>
        <span className="demoTick demoTickOpen" aria-hidden="true" />
        <span className="demoRowTitle">Checkout release QA sign-off</span>
        <span className="demoColWho">
          <i className="demoAv demoAvPR" aria-hidden="true">
            PR
          </i>
          Priya Raman
        </span>
        <span className="demoColDue isLate">Tue, Sep 22</span>
        <span className="demoRowNote demoLate" style={{ "--d": "0.85s" }}>
          <b>2 days overdue</b> · no update since Monday
        </span>
      </div>

      <div className="demoRow demoIn" style={{ "--d": "0.25s" }}>
        <span className="demoTick demoTickOpen" aria-hidden="true" />
        <span className="demoRowTitle">Payment retry bug (CHK-214)</span>
        <span className="demoColWho">
          <i className="demoAv demoAvPR" aria-hidden="true">
            PR
          </i>
          Priya Raman
        </span>
        <span className="demoColDue">Mon, Sep 28</span>
      </div>

      <div className="demoRow demoIn" style={{ "--d": "0.35s" }}>
        <span className="demoTick demoTickOpen" aria-hidden="true" />
        <span className="demoRowTitle">
          Northwind renewal: security questionnaire
        </span>
        <span className="demoColWho">
          <i className="demoAv demoAvDW" aria-hidden="true">
            DW
          </i>
          Dana Whitfield
        </span>
        <span className="demoColDue">Fri, Sep 25</span>
      </div>
    </div>
  );
}

/* 02 - planner, critic, confidence gate */
function BeatDecide() {
  return (
    <div className="demoPanel demoDecide">
      <div className="demoDecRow demoIn" style={{ "--d": "0.05s" }}>
        <span className="demoTick demoTickOk" aria-hidden="true" />
        <span className="demoKey">planned</span>
        <code className="demoCode">chase_status_update</code>
        <span className="demoVal">→ Priya Raman · Teams DM</span>
      </div>

      <div className="demoDecRow demoIn" style={{ "--d": "0.35s" }}>
        <span className="demoTick demoTickOk" aria-hidden="true" />
        <span className="demoKey">checked</span>
        <span className="demoVal">
          critic: in the action vocabulary · no duplicate ping in 24h · business
          hours
        </span>
      </div>

      <div className="demoGate demoIn" style={{ "--d": "0.6s" }}>
        <div className="demoGateTop">
          <span className="demoKey">confidence 0.86</span>
        </div>
        <div className="demoMeter" aria-hidden="true">
          <i className="demoMeterFill" />
          <i className="demoMeterMark" />
        </div>
        <div className="demoGateScale">
          <span className="demoGateTickLabel demoDim">gate at 0.75</span>
        </div>
        <div className="demoGateFoot">
          <span className="demoPill isAuto">auto</span>
          <span className="demoDim">
            clears the gate, so it runs without asking and is written to the
            audit log
          </span>
        </div>
      </div>
    </div>
  );
}

/* 03 - it chases the owner */
function BeatChase() {
  return (
    <div className="demoPanel demoChat">
      <div className="demoMsg demoIn" style={{ "--d": "0.1s" }}>
        <i className="demoAv demoAvBD" aria-hidden="true">
          BD
        </i>
        <div className="demoBubble">
          <div className="demoMsgHead">
            <b>BossDrop</b>
            <span>9:02 AM</span>
          </div>
          <p>
            Checkout release QA sign-off was due Tuesday. What is blocking it,
            and when should I expect the next update?
          </p>
        </div>
      </div>

      <div className="demoMsg isReply demoIn" style={{ "--d": "0.7s" }}>
        <i className="demoAv demoAvPR" aria-hidden="true">
          PR
        </i>
        <div className="demoBubble">
          <div className="demoMsgHead">
            <b>Priya Raman</b>
            <span>9:14 AM</span>
          </div>
          <p>
            Waiting on vendor SSO keys from Taylor. Blocked since Tuesday.
            Should clear Friday.
          </p>
        </div>
      </div>
    </div>
  );
}

/* 04 - a task, and an escalation that a human approves */
function BeatAct() {
  return (
    <div className="demoPanel demoActs">
      <article className="demoAct demoIn" style={{ "--d": "0.1s" }}>
        <div className="demoActHead">
          <img src={asanaLogo} alt="" aria-hidden="true" />
          <span className="demoActKind">Task updated</span>
          <span className="demoPill isAuto">auto</span>
        </div>
        <div className="demoActTitle">Vendor SSO keys → staging</div>
        <div className="demoActMeta">
          <span>
            <i className="demoAv demoAvTM" aria-hidden="true">
              TM
            </i>
            Taylor Mendes
          </span>
          <span>Due Fri, Sep 25</span>
          <span className="demoDim">
            dedup guard: matched existing task, owner + date set
          </span>
        </div>
      </article>

      <article className="demoAct isHold demoIn" style={{ "--d": "0.1s" }}>
        <div className="demoActHead">
          <i className="demoMark" aria-hidden="true">
            BD
          </i>
          <span className="demoActKind">Escalation</span>
          <span className="demoPill isHoldPill">confidence 0.61</span>
        </div>
        <div className="demoActTitle">
          Vendor SSO keys stuck 2 days → Dana Whitfield, VP Engineering
        </div>
        <div className="demoApprove">
          <span className="demoApproveQ">Approval queue</span>
          <span className="demoApproveSwap">
            <span className="demoApproveWait" style={{ "--dOut": "0.7s" }}>
              Waiting on a human
            </span>
            <span className="demoApproveDone" style={{ "--d": "0.85s" }}>
              <b>Approved</b> by Sam Okafor · 9:21 AM
            </span>
          </span>
        </div>
      </article>
    </div>
  );
}

/* 05 - Friday, the brief lands */
function BeatBrief() {
  return (
    <div className="demoPanel demoMail">
      <div className="demoMailBar demoIn" style={{ "--d": "0.05s" }}>
        <div className="demoMailRow">
          <span className="demoMailFrom">
            <i className="demoAv demoAvBD" aria-hidden="true">
              BD
            </i>
            BossDrop &lt;ai@bossdrop.ai&gt;
          </span>
          <span className="demoDim">Fri 7:00&nbsp;AM</span>
        </div>
        <div className="demoMailSubj">
          Weekly Executive Summary: week of Sep&nbsp;21
        </div>
      </div>

      {/* The real Friday brief in miniature (backend utils/execReportEmail.js):
          the header with its tally, the lead line, workstreams with owners and
          status pills beside the risks and decisions, and the footer. */}
      <div className="demoLetter demoBrief demoIn" style={{ "--d": "0.3s" }}>
        <div className="demoBriefHead">
          <div>
            <div className="demoBriefEyebrow">BossDrop · Friday, September&nbsp;25</div>
            <div className="demoBriefTitle">Weekly Executive Summary</div>
          </div>
          <div className="demoBriefTally">
            <span className="demoBriefPill isOn">2 on track</span>
            <span className="demoBriefPill isRisk">1 at risk</span>
            <span className="demoBriefPill isStuck">1 stuck</span>
          </div>
        </div>
        <div className="demoBriefLead">Four workstreams, two need attention.</div>
        <div className="demoBriefCols">
          <div className="demoBriefCol demoBriefWork">
            <div className="demoBriefLabel">Executive summary</div>
            <div className="demoBriefRow">
              <span>
                Vendor SSO keys to staging
                <small>
                  Taylor Mendes <i>Asana</i>
                </small>
              </span>
              <span className="demoBriefPill isStuck">Stuck</span>
            </div>
            <div className="demoBriefRow">
              <span>
                Checkout release
                <small>
                  Priya Raman <i>Asana</i>
                </small>
              </span>
              <span className="demoBriefPill isRisk">At risk</span>
            </div>
            <div className="demoBriefRow">
              <span>
                Q4 pricing page
                <small>Dana Whitfield</small>
              </span>
              <span className="demoBriefPill isOn">On track</span>
            </div>
          </div>
          <div className="demoBriefCol">
            <div className="demoBriefLabel">Risks</div>
            <ul>
              <li>
                Vendor SSO keys stuck 3 days, escalated to Dana Whitfield
                Thursday
                <small>Taylor Mendes</small>
              </li>
            </ul>
            <div className="demoBriefLabel">Decisions needed</div>
            <ul>
              <li>
                Approve the checkout release slipping to Tuesday
                <small>Priya Raman</small>
              </li>
            </ul>
          </div>
        </div>
        <div className="demoBriefFoot">
          <span>On this brief: Taylor Mendes, Priya Raman, Dana Whitfield, +1</span>
          <span>Sent automatically by BossDrop</span>
        </div>
      </div>
    </div>
  );
}
